import { createContext, createElement, useContext, useEffect, useMemo, useRef, useSyncExternalStore } from 'react'
import { prepacksApi } from '@/services/api'

export const PrepackRunQueueContext = createContext(null)

const TERMINAL_STATUSES = new Set(['success', 'partial', 'failed', 'skipped', 'unknown'])
const RUN_STATUS_MAP = {
  success: 'success',
  partial: 'partial',
  failed: 'failed',
  skipped: 'skipped',
}

function isTerminalRun(run) {
  return TERMINAL_STATUSES.has(run?.status)
}

function runStatus(run) {
  return RUN_STATUS_MAP[run?.status] || 'unknown'
}

function runMessage(run) {
  const status = runStatus(run)
  return run?.details?.progress?.message
    || run?.details?.message
    || (status === 'success'
      ? `Successfully created ${run?.prepacks_created ?? 0} prepacks`
      : run?.error_message || run?.status || 'Run finished')
}

function fresherRun(runA, runB) {
  if (!runA) return runB
  if (!runB) return runA
  if (isTerminalRun(runA) && !isTerminalRun(runB)) return runA
  if (isTerminalRun(runB) && !isTerminalRun(runA)) return runB

  const timeA = Date.parse(runA.updated_at || runA.created_at || 0)
  const timeB = Date.parse(runB.updated_at || runB.created_at || 0)
  if (timeA && timeB && timeA !== timeB) return timeA > timeB ? runA : runB
  if (timeA && !timeB) return runA
  if (timeB && !timeA) return runB
  return runB
}

export function generateRequestId() {
  const cryptoObj = (typeof crypto !== 'undefined' && crypto)
    || (typeof globalThis !== 'undefined' && globalThis.crypto)

  if (cryptoObj?.randomUUID) {
    return cryptoObj.randomUUID()
  }

  if (cryptoObj?.getRandomValues) {
    const bytes = new Uint8Array(16)
    cryptoObj.getRandomValues(bytes)
    bytes[6] = (bytes[6] & 0x0f) | 0x40 // RFC4122 version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80 // RFC4122 variant 1
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }

  throw new Error('Crypto API unavailable for generating request_id')
}

/**
 * Whether a run's failed request leaves it unknown whether the server started
 * the run: no reply, a gateway error or a timeout.
 */
export function isAmbiguousRunFailure(error) {
  return !error?.response
    || [502, 503, 504].includes(error?.response?.status)
    || error?.code === 'ECONNABORTED'
    || String(error?.message || '').toLowerCase().includes('timeout')
}

/** Whether a run is the manual run a request id started: its slot is `manual:<id>`. */
export function isRunOfRequest(run, requestId) {
  return run.details?.slot === `manual:${requestId}` || run.details?.request_id === requestId
}

export class PrepackRunQueueStore {
  constructor(initialConfig = {}) {
    this.entries = []
    this.activeJobId = null
    this.isProcessing = false
    this.isDestroyed = false
    this.isSuspended = false
    this.activePollInterval = null
    this.unmountTimer = null
    this.listeners = new Set()
    this.slug = initialConfig.slug || null
    this.canOperate = Boolean(initialConfig.canOperate && this.slug)
    this.canPublish = Boolean(initialConfig.canPublish && this.slug)
    this.entryCounter = 0
    this.executionGeneration = 0
    this.activeExecution = null
  }
  subscribe = (listener) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
  notify = () => {
    for (const listener of this.listeners) {
      listener()
    }
  }

  getSnapshot = () => {
    return this.entries
  }

  activate = () => {
    clearTimeout(this.unmountTimer)
    this.unmountTimer = null
    this.isDestroyed = false
    const wasSuspended = this.isSuspended
    this.isSuspended = false
    if (wasSuspended && !this.isProcessing && this.slug && this.canOperate && this.entries.some((e) => e.status === 'queued')) {
      this.processQueue()
    }
  }

  scheduleDestroy = () => {
    this.isSuspended = true
    clearTimeout(this.unmountTimer)
    // Defer irreversible disposal so StrictMode replay doesn't permanently kill the queue
    this.unmountTimer = setTimeout(() => {
      this.destroy()
    }, 50)
  }

  cancelActiveExecution = () => {
    const execution = this.activeExecution
    if (!execution) return
    execution.cancelled = true
    execution.isPolling = false
    clearTimeout(execution.recoveryTimer)
    execution.recoveryTimer = null
    for (const resolve of execution.terminalWaiters) resolve(null)
    execution.terminalWaiters.clear()
    if (execution.pollController) execution.pollController.abort()
    if (execution.postController) execution.postController.abort()
    if (this.activePollInterval === execution.pollInterval) {
      clearInterval(this.activePollInterval)
      this.activePollInterval = null
    }
    this.activeExecution = null
    this.isProcessing = false
    this.activeJobId = null
  }

  destroy = () => {
    clearTimeout(this.unmountTimer)
    this.unmountTimer = null
    const cancelledEntryId = this.activeExecution?.entryId || null
    this.executionGeneration += 1
    this.cancelActiveExecution()
    this.isDestroyed = true
    this.isSuspended = true

    let changed = false
    this.entries = this.entries.map((entry) => {
      const isActiveCancellation = entry.id === cancelledEntryId && entry.status === 'running'
      if (entry.status === 'queued' || isActiveCancellation) {
        changed = true
        return {
          ...entry,
          status: 'skipped',
          message: 'Cancelled due to workspace unmount',
        }
      }
      return entry
    })
    if (changed) this.notify()
  }

  setOrgConfig = ({ slug, canOperate = true, canPublish = true } = {}) => {
    clearTimeout(this.unmountTimer)
    this.unmountTimer = null
    this.isDestroyed = false

    const normalizedSlug = slug || null
    const orgChanged = Boolean(this.slug && this.slug !== normalizedSlug)
    this.slug = normalizedSlug
    this.canOperate = Boolean(canOperate && normalizedSlug)
    this.canPublish = Boolean(canPublish && normalizedSlug)
    this.isSuspended = false

    if (orgChanged) {
      // A queue entry has no portable tenant identity. Drop old-org entries
      // rather than rendering them in the newly selected organization.
      this.executionGeneration += 1
      this.cancelActiveExecution()
      clearInterval(this.activePollInterval)
      this.activePollInterval = null
      if (this.entries.length > 0) {
        this.entries = []
        this.notify()
      }
      return
    }

    if (!normalizedSlug) {
      this.executionGeneration += 1
      this.cancelActiveExecution()
      clearInterval(this.activePollInterval)
      this.activePollInterval = null
      if (this.entries.length > 0) {
        this.entries = []
        this.notify()
      }
    } else if (!this.canOperate) {
      // Stop undispatched work if cannot operate.
      let changed = false
      this.entries = this.entries.map((entry) => {
        if (entry.status === 'queued') {
          changed = true
          return {
            ...entry,
            status: 'failed',
            message: 'Operate permission required',
            error: 'Permission denied',
          }
        }
        return entry
      })
      if (changed) this.notify()
    } else if (!this.isProcessing && this.entries.some((entry) => entry.status === 'queued')) {
      this.processQueue()
    }
  }

  updateEntry = (id, patch) => {
    this.entries = this.entries.map((e) => (e.id === id ? { ...e, ...patch } : e))
    this.notify()
  }

  isPending = (automationId) => {
    return this.entries.some(
      (e) => e.automationId === automationId && (e.status === 'queued' || e.status === 'running')
    )
  }

  enqueue = (job) => {
    if (this.isDestroyed || !job) return null
    const automationId = job.id || job.automationId
    if (!automationId) return null

    // Duplicates of pending same job prevented
    if (this.isPending(automationId)) {
      return null
    }

    this.entryCounter += 1
    const entryId = `run_entry_${Date.now()}_${this.entryCounter}`
    const requestId = generateRequestId()
    const newEntry = {
      id: entryId,
      requestId,
      automationId,
      name: job.name || 'Schedule run',
      autoPublish: Boolean(job.autoPublish || job.auto_publish),
      status: 'queued',
      message: 'Queued',
      startedAt: null,
    }

    this.entries = [...this.entries, newEntry]
    this.notify()
    this.processQueue()
    return entryId
  }

  clearCompleted = () => {
    this.entries = this.entries.filter((e) => !TERMINAL_STATUSES.has(e.status))
    this.notify()
  }

  processQueue = async () => {
    if (this.isDestroyed || this.isSuspended || this.isProcessing) return
    const currentSlug = this.slug
    if (!currentSlug || !this.canOperate) return

    const nextEntry = this.entries.find((entry) => entry.status === 'queued')
    if (!nextEntry) return

    this.isProcessing = true
    this.activeJobId = nextEntry.id

    if (nextEntry.autoPublish && !this.canPublish) {
      this.updateEntry(nextEntry.id, {
        status: 'failed',
        message: 'Publish permission is required to run a schedule that publishes automatically',
        error: 'Permission denied',
      })
      this.isProcessing = false
      this.activeJobId = null
      setTimeout(() => this.processQueue(), 0)
      return
    }

    const execution = {
      generation: ++this.executionGeneration,
      entryId: nextEntry.id,
      slug: currentSlug,
      isPolling: true,
      pollInFlight: false,
      pollPromise: null,
      postController: new AbortController(),
      pollInterval: null,
      recoveryTimer: null,
      terminalWaiters: new Set(),
      terminalRun: null,
      cancelled: false,
    }
    this.activeExecution = execution

    const isCurrent = () => (
      !execution.cancelled
      && !this.isDestroyed
      && this.activeExecution === execution
      && this.executionGeneration === execution.generation
      && this.slug === execution.slug
    )
    const stopPolling = (abort = true) => {
      execution.isPolling = false
      clearTimeout(execution.recoveryTimer)
      execution.recoveryTimer = null
      if (execution.pollInterval && this.activePollInterval === execution.pollInterval) {
        clearInterval(execution.pollInterval)
        this.activePollInterval = null
      }
      if (abort && execution.pollController) execution.pollController.abort()
    }
    const setTerminal = (run) => {
      if (!isCurrent() || !isTerminalRun(run)) return
      execution.terminalRun = fresherRun(execution.terminalRun, run)
      stopPolling(false)
      // A confirmed server terminal result wins the POST race. Abort the
      // client request so the queue can dispatch the next entry immediately.
      execution.postController?.abort()
      this.updateEntry(execution.entryId, {
        status: runStatus(execution.terminalRun),
        message: runMessage(execution.terminalRun),
        run: execution.terminalRun,
      })
      for (const resolve of execution.terminalWaiters) resolve(execution.terminalRun)
      execution.terminalWaiters.clear()
    }
    const findExactRun = (runs, boundRunId) => {
      if (boundRunId) return runs.find((run) => run.id === boundRunId) || null
      return runs.find((run) => isRunOfRequest(run, nextEntry.requestId)) || null
    }
    const pollRuns = () => {
      if (!isCurrent() || !execution.isPolling) return null
      if (execution.pollInFlight) return execution.pollPromise

      execution.pollInFlight = true
      execution.pollController = new AbortController()
      const params = () => (execution.terminalRun?.id
        ? { run_id: execution.terminalRun.id }
        : execution.boundRunId
          ? { run_id: execution.boundRunId }
          : { request_id: nextEntry.requestId })

      execution.pollPromise = (async () => {
        try {
          const response = await prepacksApi.listAutomationRuns(
            execution.slug,
            params(),
            { signal: execution.pollController.signal },
          )
          if (!isCurrent() || !execution.isPolling) return null
          const runs = Array.isArray(response)
            ? response
            : Array.isArray(response?.runs)
              ? response.runs
              : []
          const matchingRun = findExactRun(runs, execution.boundRunId)
          if (!matchingRun) return null
          if (matchingRun.id && !execution.boundRunId) execution.boundRunId = matchingRun.id
          execution.latestPolledRun = fresherRun(execution.latestPolledRun, matchingRun)
          if (isTerminalRun(matchingRun)) {
            setTerminal(matchingRun)
          } else if (isCurrent()) {
            const currentEntry = this.entries.find((entry) => entry.id === execution.entryId)
            const progress = matchingRun.details?.progress
            const stageMessage = progress?.message || matchingRun.details?.message
            if (currentEntry && currentEntry.status === 'running') {
              this.updateEntry(execution.entryId, {
                ...(stageMessage ? { message: stageMessage } : {}),
                run: matchingRun,
              })
            }
          }
          return matchingRun
        } catch (error) {
          if (!error?.name?.toLowerCase?.().includes('abort')) {
            // A transient poll failure must not change the execution state.
          }
          return null
        } finally {
          execution.pollInFlight = false
          execution.pollController = null
          execution.pollPromise = null
        }
      })()
      return execution.pollPromise
    }
    const waitForTerminal = (timeout = 120000) => {
      if (execution.terminalRun) return Promise.resolve(execution.terminalRun)
      return new Promise((resolve) => {
        let settled = false
        const finish = (run) => {
          if (settled) return
          settled = true
          clearTimeout(execution.recoveryTimer)
          execution.recoveryTimer = null
          execution.terminalWaiters.delete(finish)
          resolve(run)
        }
        execution.terminalWaiters.add(finish)
        execution.recoveryTimer = setTimeout(() => {
          stopPolling()
          finish(null)
        }, timeout)
      })
    }

    const finishUnknown = (errorMsg, run = undefined) => {
      if (!isCurrent() || execution.terminalRun) return
      this.updateEntry(execution.entryId, {
        status: 'unknown',
        error: errorMsg,
        message: 'Run status unknown (transport failure). Check the library before retrying.',
        ...(run ? { run } : {}),
      })
    }

    this.updateEntry(nextEntry.id, {
      status: 'running',
      startedAt: new Date().toISOString(),
      message: 'Starting run…',
    })
    execution.pollInterval = setInterval(pollRuns, 1000)
    this.activePollInterval = execution.pollInterval

    const terminalPromise = waitForTerminal()

    try {
      const postPromise = prepacksApi.runAutomation(execution.slug, nextEntry.automationId, {
        requestId: nextEntry.requestId,
        timeout: 120000,
        signal: execution.postController.signal,
      }).then(
        (result) => ({ kind: 'post', result }),
        (error) => ({ kind: 'error', error }),
      )
      const outcome = await Promise.race([
        postPromise,
        terminalPromise.then((run) => run
          ? { kind: 'terminal', run }
          : { kind: 'timeout' }),
      ])

      if (!isCurrent() || execution.terminalRun) return
      if (outcome.kind === 'terminal') return
      if (outcome.kind === 'timeout') {
        finishUnknown('The server returned no terminal run before polling stopped.')
        execution.postController.abort()
        return
      }

      if (outcome.kind === 'error') {
        const error = outcome.error
        const errorMsg = error?.response?.data?.error
          || error?.response?.data?.message
          || error?.message
          || 'Execution failed'

        // Probe the exact request slot/run once. If the server accepted the
        // request and it is still running, keep the bounded terminal poll
        // alive rather than falsely finishing or retrying the run.
        const matchingRun = await pollRuns()
        if (!isCurrent() || execution.terminalRun) return
        if (matchingRun && !isTerminalRun(matchingRun)) {
          const terminalRun = await terminalPromise
          if (!terminalRun && isCurrent()) finishUnknown(errorMsg, matchingRun)
          return
        }

        if (isAmbiguousRunFailure(error)) {
          finishUnknown(errorMsg, execution.latestPolledRun)
        } else {
          this.updateEntry(execution.entryId, {
            status: 'failed',
            error: errorMsg,
            message: errorMsg,
          })
        }
        return
      }

      const result = outcome.result
      if (result?.id && !execution.boundRunId) execution.boundRunId = result.id
      if (isTerminalRun(result)) {
        const terminalResult = execution.terminalRun && !result.updated_at && !result.created_at
          ? execution.terminalRun
          : fresherRun(execution.terminalRun, result)
        setTerminal(terminalResult)
        return
      }

      const currentEntry = this.entries.find((entry) => entry.id === execution.entryId)
      if (currentEntry && currentEntry.status === 'running') {
        this.updateEntry(execution.entryId, {
          message: runMessage(result),
          run: result,
        })
      }
      const terminalRun = await terminalPromise
      if (!terminalRun && isCurrent()) {
        finishUnknown('The server returned a non-terminal run status before polling stopped.', result)
      }
    } finally {
      if (this.activeExecution === execution) {
        execution.cancelled = true
        stopPolling()
        clearTimeout(execution.recoveryTimer)
        this.activeExecution = null
        this.isProcessing = false
        this.activeJobId = null
        setTimeout(() => this.processQueue(), 0)
      }
    }
  }
}

export function PrepackRunQueueProvider({ children, slug, canOperate = true, canPublish = true }) {
  const storeRef = useRef(null)
  if (!storeRef.current) {
    storeRef.current = new PrepackRunQueueStore({ slug, canOperate, canPublish })
  }
  const store = storeRef.current

  useEffect(() => {
    store.setOrgConfig({ slug, canOperate, canPublish })
  }, [store, slug, canOperate, canPublish])

  useEffect(() => {
    store.activate()
    return () => {
      store.scheduleDestroy()
    }
  }, [store])

  const entries = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)

  const contextValue = useMemo(() => ({
    entries,
    enqueue: store.enqueue,
    isPending: store.isPending,
    clearCompleted: store.clearCompleted,
  }), [entries, store])

  return createElement(PrepackRunQueueContext.Provider, { value: contextValue }, children)
}

export function usePrepackRunQueue() {
  const context = useContext(PrepackRunQueueContext)
  if (!context) {
    throw new Error('usePrepackRunQueue must be used within a PrepackRunQueueProvider')
  }
  return context
}

export default usePrepackRunQueue
